import { ArrowRight, Linkedin, type LucideIcon, Mail } from 'lucide-react';
import { z } from 'zod';

import { cn } from '@/lib/shadcn/utils';
import { isLinkedInProfileUrl } from '@/modules/careers/careers';
import type { JobApplicationRequestApi } from '@/modules/careers/types';
import { Form } from '@/modules/forms/components/Form';
import { FormInput } from '@/modules/forms/components/FormInput';
import { FormTextarea } from '@/modules/forms/components/FormTextarea';
import { Button } from '@/modules/ui/components/button/Button';
import { InlineError } from '@/modules/ui/components/inline-error/InlineError';

// Limits mirror JobApplicationRequest in finlay-api.
const applicationSchema = z.object({
  firstName: z
    .string()
    .trim()
    .min(1, 'Enter your first name.')
    .max(100, 'Use at most 100 characters.'),
  lastName: z
    .string()
    .trim()
    .min(1, 'Enter your last name.')
    .max(100, 'Use at most 100 characters.'),
  email: z
    .string()
    .trim()
    .min(1, 'Enter your email.')
    .email('Enter an email like name@example.com.')
    .max(320, 'Use at most 320 characters.'),
  linkedInProfileUrl: z
    .string()
    .trim()
    .min(1, 'Enter your LinkedIn profile URL.')
    .max(500, 'Use at most 500 characters.')
    .refine(
      isLinkedInProfileUrl,
      'Enter a LinkedIn profile URL like linkedin.com/in/your-name.',
    ),
  motivation: z.string().trim().max(5000, 'Use at most 5,000 characters.'),
});

const defaultValues: z.input<typeof applicationSchema> = {
  firstName: '',
  lastName: '',
  email: '',
  linkedInProfileUrl: '',
  motivation: '',
};

// 16px on phones keeps iOS from zooming into the field on focus.
const fieldClassName = 'h-11 rounded-[10px] px-3.5 text-base md:text-[15px]';

interface JobApplicationFormProps {
  /** Names the company in the optional motivation question. */
  companyName: string;
  privacyNoticeUrl: string;
  isPending: boolean;
  /** The failed submission's message, shown under the form. */
  error: string | null;
  onSubmit: (application: JobApplicationRequestApi) => void;
}

/**
 * The public application form for one job. Validation runs on submit so an
 * applicant who is still typing is not interrupted.
 */
export function JobApplicationForm({
  companyName,
  privacyNoticeUrl,
  isPending,
  error,
  onSubmit,
}: JobApplicationFormProps) {
  // The schema has already trimmed every field.
  const handleSubmit = (values: z.output<typeof applicationSchema>) =>
    onSubmit({ ...values, motivation: values.motivation || null });

  return (
    <Form
      defaultValues={defaultValues}
      mode="onSubmit"
      noValidate
      onSubmit={handleSubmit}
      schema={applicationSchema}
    >
      {/* Tighter label-to-field spacing than the app's default FormItem gap. */}
      <div className="flex flex-col gap-5 [&_[data-slot=form-item]]:gap-2">
        <div className="grid gap-5 sm:grid-cols-2">
          <FormInput
            autoComplete="given-name"
            autoFocus
            className={fieldClassName}
            disabled={isPending}
            label="First name"
            name="firstName"
            required
          />
          <FormInput
            autoComplete="family-name"
            className={fieldClassName}
            disabled={isPending}
            label="Last name"
            name="lastName"
            required
          />
        </div>
        <FormInput
          autoComplete="email"
          className={cn(fieldClassName, 'pl-10')}
          disabled={isPending}
          label="Email"
          name="email"
          required
          placeholder="you@example.com"
          startAdornment={<FieldIcon icon={Mail} />}
          type="email"
        />
        <FormInput
          autoComplete="url"
          className={cn(fieldClassName, 'pl-10')}
          description="The team will reach out to you on LinkedIn."
          disabled={isPending}
          label="LinkedIn profile"
          name="linkedInProfileUrl"
          required
          placeholder="linkedin.com/in/your-name"
          startAdornment={<FieldIcon icon={Linkedin} />}
          type="url"
        />
        <FormTextarea
          className="min-h-28 rounded-[10px] px-3.5 py-3 text-base md:text-[15px]"
          disabled={isPending}
          label={`What draws you to ${companyName}?`}
          labelSuffix={
            <span className="text-muted-foreground text-[13px]">Optional</span>
          }
          name="motivation"
          placeholder="A sentence or two is plenty."
          rows={4}
        />
        {error && <InlineError>{error}</InlineError>}
        <div className="flex flex-col gap-3 pt-1">
          <Button
            className="h-12 w-full rounded-xl text-[15px] font-semibold [&>svg]:transition-transform [&>svg]:duration-150 [&>svg]:ease-out hover:[&>svg]:translate-x-0.5"
            loading={isPending}
            type="submit"
          >
            Submit application
            {!isPending && <ArrowRight aria-hidden />}
          </Button>
          <p className="text-muted-foreground text-center text-xs leading-5">
            We process your application as described in our{' '}
            <a
              className="text-foreground hover:text-primary font-medium underline underline-offset-2 transition-colors"
              href={privacyNoticeUrl}
              rel="noopener noreferrer"
              target="_blank"
            >
              Privacy Notice
            </a>
            .
          </p>
        </div>
      </div>
    </Form>
  );
}

function FieldIcon({ icon: Icon }: { icon: LucideIcon }) {
  return (
    <Icon
      aria-hidden
      className="text-input-placeholder pointer-events-none absolute top-1/2 left-3.5 size-4 -translate-y-1/2"
    />
  );
}
